<template>
  <div class="calendar-widget" :style="containerStyle">
    <div v-if="loading" class="calendar-loading">
      <i class="material-icons-outlined rotating">refresh</i>
      <div class="loading-message">{{ loadingMessage }}</div>
    </div>

    <div
      v-else-if="visibleEvents.length === 0 && !calendarConfig.useDemoData"
      class="calendar-no-events"
    >
      <i class="material-icons-outlined align-middle">event_note</i>
      <div class="no-events-message">{{ noEventsMessage }}</div>
    </div>

    <!-- List View -->
    <template v-else>
      <div v-if="displayMode === 'list'" class="calendar-events">
        <div
          v-for="(event, index) in visibleEvents"
          :key="index"
          class="calendar-event-card"
          :style="getEventCardStyle(event)"
        >
          <div class="calendar-event-date" :style="getEventDateStyle(event)">
            <div class="date-month" :style="listDateStyle">{{ event.dateMonth }}</div>
            <div class="date-day" :style="getListDateDayStyle(event)">{{ event.dateDay }}</div>
            <div class="date-weekday" :style="listDateStyle">{{ event.dateWeekday }}</div>
          </div>

          <div class="calendar-event-content" :style="eventContentStyle">
            <div class="event-title" :style="eventTitleStyle">{{ event.title }}</div>
            <div class="event-time" :style="eventTimeStyle">
              {{ event.start }} - {{ event.end }}
            </div>
            <div class="event-description" :style="eventDescriptionStyle">{{ event.subtitle }}</div>
          </div>
        </div>
      </div>

      <!-- Calendar Grid View -->
      <div v-else class="calendar-grid" :data-days="daysToShow">
        <div
          v-for="(day, index) in calendarDays"
          :key="index"
          class="calendar-day-column"
          :style="getDayColumnStyle(day)"
        >
          <div class="day-header">
            <div class="day-weekday" :style="getWeekdayHeaderStyle(day)">{{ day.weekday }}</div>
            <div class="day-date" :style="getDateHeaderStyle(day)">
              {{ day.day }} {{ day.month }}
            </div>
          </div>

          <div class="day-events">
            <div
              v-for="(event, eventIndex) in day.events"
              :key="eventIndex"
              class="calendar-grid-event"
              :style="eventCardStyle"
            >
              <div class="grid-event-title" :style="eventTitleStyle">{{ event.title }}</div>
              <div class="grid-event-time" :style="eventTimeStyle">
                {{ event.start }} - {{ event.end }}
              </div>
              <div class="grid-event-description" :style="eventDescriptionStyle">
                {{ event.subtitle }}
              </div>
            </div>

            <div v-if="day.events.length === 0" class="no-events-day">
              <i class="material-icons-outlined">event_note</i>
              <div class="no-events-message">No events</div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  import ICAL from 'ical.js';

  import {
    calendarDefaultOptions,
    CALENDAR_LANGUAGES,
    CALENDAR_MESSAGES,
    CALENDAR_DISPLAY_MODE,
    CALENDAR_TIME_FORMAT,
    DEFAULT_FETCH_INTERVAL,
  } from '@/constants/calendarWidget';
  import { generateCalendarDemoData } from '@/helpers/calendarWidget';
  import { hex8ToRgba, isHex8 } from '@/helpers/utils';
  import { apiFetchICSFile } from '@/api/calendar';
  import { logger } from '@/utils/screenEventsLogger';

  export default {
    name: 'CalendarView',

    props: {
      app: {
        type: Object,
        required: true,
      },
    },

    data() {
      return {
        now: new Date(),
        timer: null,
        fetchTimer: null,
        icsEvents: [],
        loading: false,
      };
    },

    mounted() {
      this.updateNow();
      this.timer = setInterval(this.updateNow, 60 * 1000);
      this.fetchICSFile();
      this.startFetchTimer();
    },

    beforeDestroy() {
      if (this.timer) {
        clearInterval(this.timer);
      }

      if (this.fetchTimer) {
        clearInterval(this.fetchTimer);
      }
    },

    methods: {
      updateNow() {
        this.now = new Date();
      },

      startFetchTimer() {
        if (this.fetchTimer) {
          clearInterval(this.fetchTimer);
        }

        this.fetchTimer = setInterval(this.fetchICSFile, DEFAULT_FETCH_INTERVAL * 60 * 1000);
      },

      async fetchICSFile() {
        const config = this.calendarConfig;
        const icsFileContent = config.icsFileContent;
        const icsUrl = config.icsUrl;
        const useDemoData = config.useDemoData;
        const sourceType = config.sourceType;

        if (useDemoData) {
          this.icsEvents = generateCalendarDemoData();
          return;
        }

        if (sourceType === 'file') {
          if (!icsFileContent) {
            this.icsEvents = [];
            return;
          }

          if (!this.icsEvents.length) {
            this.loading = true;
          }

          try {
            if (!icsFileContent.includes('BEGIN:VCALENDAR')) {
              throw new Error('Invalid ICS file: missing VCALENDAR data');
            }

            this.parseICS(icsFileContent);
          } catch (err) {
            logger.addLog('Error parsing ICS file: ' + err);
            if (!this.icsEvents.length) {
              this.icsEvents = [];
            }
          } finally {
            this.loading = false;
          }
          return;
        }

        if (!icsUrl) {
          this.icsEvents = [];
          return;
        }

        if (!this.icsEvents.length) {
          this.loading = true;
        }

        const icsText = await apiFetchICSFile(icsUrl);

        if (icsText) {
          this.parseICS(icsText);
        } else if (!this.icsEvents.length) {
          this.icsEvents = [];
        }

        this.loading = false;
      },

      isToday(date) {
        if (!date) return false;
        const eventDate = moment(date);
        const today = moment(this.now);
        return eventDate.isSame(today, 'day');
      },

      parseICS(icsText) {
        try {
          const jcalData = ICAL.parse(icsText);
          const comp = new ICAL.Component(jcalData);
          const vevents = comp.getAllSubcomponents('vevent');

          const now = new Date();
          const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const maxFutureDate = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
          const maxFutureICAL = ICAL.Time.fromJSDate(maxFutureDate);

          const filteredEvents = [];

          const mastersByUid = new Map();
          const exceptionsByUid = new Map();

          for (let i = 0; i < vevents.length; i++) {
            const event = new ICAL.Event(vevents[i]);
            const uid = event.uid;

            if (event.isRecurrenceException()) {
              if (!exceptionsByUid.has(uid)) exceptionsByUid.set(uid, []);
              exceptionsByUid.get(uid).push(event);
            } else {
              mastersByUid.set(uid, event);
            }
          }

          const pushEvent = (dateStart, dateEnd, title, description) => {
            filteredEvents.push({
              startDate: dateStart.toJSDate(),
              endDate: dateEnd.toJSDate(),
              displayStartDate: new Date(
                Date.UTC(
                  dateStart.year,
                  dateStart.month - 1,
                  dateStart.day,
                  dateStart.hour,
                  dateStart.minute,
                  dateStart.second,
                ),
              ),
              displayEndDate: new Date(
                Date.UTC(
                  dateEnd.year,
                  dateEnd.month - 1,
                  dateEnd.day,
                  dateEnd.hour,
                  dateEnd.minute,
                  dateEnd.second,
                ),
              ),
              title: title || 'Untitled Event',
              description: description || '',
            });
          };

          for (const [uid, event] of mastersByUid) {
            const exceptions = exceptionsByUid.get(uid) || [];
            for (const exc of exceptions) {
              event.relateException(exc);
            }

            if (event.isRecurring()) {
              const iter = event.iterator();
              let next;
              while ((next = iter.next())) {
                if (next.compare(maxFutureICAL) > 0) break;

                const occurrence = event.getOccurrenceDetails(next);
                const occEndDate = occurrence.endDate.toJSDate();
                const occEnd = occEndDate || occurrence.startDate.toJSDate();

                if (occEnd > startOfToday) {
                  pushEvent(
                    occurrence.startDate,
                    occurrence.endDate,
                    occurrence.item.summary,
                    occurrence.item.description,
                  );
                }
              }
            } else {
              const endDate = event.endDate.toJSDate();
              const eventEnd = endDate || event.startDate.toJSDate();

              if (eventEnd > startOfToday && event.startDate.toJSDate() <= maxFutureDate) {
                pushEvent(event.startDate, event.endDate, event.summary, event.description);
              }
            }
          }

          for (const [uid, exceptions] of exceptionsByUid) {
            if (!mastersByUid.has(uid)) {
              for (const exc of exceptions) {
                const endDate = exc.endDate.toJSDate();
                const eventEnd = endDate || exc.startDate.toJSDate();

                if (eventEnd > startOfToday && exc.startDate.toJSDate() <= maxFutureDate) {
                  pushEvent(exc.startDate, exc.endDate, exc.summary, exc.description);
                }
              }
            }
          }

          this.icsEvents = filteredEvents;
        } catch (err) {
          logger.addLog('Error parsing ICS: ' + err);
          this.icsEvents = [];
        }
      },
    },

    computed: {
      calendarConfig() {
        const appCalendar = this.app.calendar;
        return appCalendar || calendarDefaultOptions;
      },

      language() {
        const config = this.calendarConfig;
        return config.language || CALENDAR_LANGUAGES.SWEDISH;
      },

      timeFormat() {
        const config = this.calendarConfig;
        return config.timeFormat || CALENDAR_TIME_FORMAT.TWENTY_FOUR_HOUR;
      },

      is24HourFormat() {
        return this.timeFormat === CALENDAR_TIME_FORMAT.TWENTY_FOUR_HOUR;
      },

      containerStyle() {
        const config = this.calendarConfig;
        const bgColor = config.backgroundColor || '#FFFFFF';

        return {
          fontFamily: config.textFont || 'Poppins',
          color: config.fontColor || '#000000',
          backgroundColor: bgColor && isHex8(bgColor) ? hex8ToRgba(bgColor) : bgColor,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        };
      },

      daysToShow() {
        const config = this.calendarConfig;
        return config.daysToShow || 1;
      },

      displayMode() {
        const config = this.calendarConfig;
        return config.displayMode || CALENDAR_DISPLAY_MODE.LIST;
      },

      maxEventsPerDay() {
        const config = this.calendarConfig;
        return config.maxEventsPerDay || 5;
      },

      allEvents() {
        const config = this.calendarConfig;
        if (config.useDemoData) {
          return generateCalendarDemoData(this.language, this.timeFormat);
        }

        if (this.icsEvents.length > 0) {
          const lang = this.language;
          const timeFormatString = this.is24HourFormat ? 'HH:mm' : 'hh:mm A';

          return this.icsEvents.map((event) => {
            const start = moment(event.displayStartDate || event.startDate).locale(this.language);
            const end = moment(event.displayEndDate || event.endDate).locale(this.language);

            return {
              dateMonth: start.format('MMM').toUpperCase(),
              dateDay: start.format('D'),
              dateWeekday: start.format('dddd').toUpperCase(),
              start: start.format(timeFormatString),
              end: end.format(timeFormatString),
              title: event.title,
              subtitle: event.description || '',
              startDate: event.startDate,
              endDate: event.endDate,
            };
          });
        }

        return [];
      },

      upcomingEvents() {
        const now = moment(this.now);

        return this.allEvents
          .filter((event) => {
            if (!event.startDate) return true;
            const eventStart = moment(event.startDate);
            const eventEnd = moment(event.endDate || event.startDate);

            if (this.displayMode === 'list') {
              return eventEnd.isAfter(now);
            }

            const endOfPeriod = moment(this.now)
              .startOf('day')
              .add(this.daysToShow - 1, 'days')
              .endOf('day');

            return eventEnd.isAfter(now) && eventStart.isBefore(endOfPeriod);
          })
          .sort((a, b) => {
            if (!a.startDate || !b.startDate) return 0;
            return moment(a.startDate).diff(moment(b.startDate));
          });
      },

      maxEvents() {
        const config = this.calendarConfig;
        return config.maxEvents || 5;
      },

      visibleEvents() {
        return this.upcomingEvents.slice(0, this.maxEvents);
      },

      noEventsMessage() {
        const messages = CALENDAR_MESSAGES.NO_EVENTS;
        return messages[this.language] || messages[CALENDAR_LANGUAGES.ENGLISH];
      },

      loadingMessage() {
        const messages = CALENDAR_MESSAGES.LOADING;
        return messages[this.language] || messages[CALENDAR_LANGUAGES.ENGLISH];
      },

      calendarDays() {
        const days = [];
        const startOfToday = moment(this.now).startOf('day');
        const lang = this.language;
        const maxPerDay = this.maxEventsPerDay;

        for (let i = 0; i < this.daysToShow; i++) {
          const currentDay = moment(startOfToday).add(i, 'days');
          const dayEvents = this.upcomingEvents.filter((event) => {
            const eventStart = moment(event.startDate);
            return eventStart.isSame(currentDay, 'day');
          });

          const limitedEvents = maxPerDay ? dayEvents.slice(0, maxPerDay) : dayEvents;

          days.push({
            weekday: currentDay.locale(lang).format('dddd'),
            month: currentDay.locale(lang).format('MMMM'),
            day: currentDay.format('D'),
            events: limitedEvents,
          });
        }

        return days;
      },

      eventCardStyle() {
        const config = this.calendarConfig;
        const showBorder = config.eventBorderDisplay !== false;
        const borderWidth =
          config.eventBorderWidth !== undefined
            ? config.eventBorderWidth
            : calendarDefaultOptions.eventBorderWidth;
        const bgColor = config.eventBackgroundColor || calendarDefaultOptions.eventBackgroundColor;
        const borderColor = showBorder
          ? config.eventBorderColor || calendarDefaultOptions.eventBorderColor
          : 'transparent';

        return {
          backgroundColor: bgColor && isHex8(bgColor) ? hex8ToRgba(bgColor) : bgColor,
          borderColor: borderColor && isHex8(borderColor) ? hex8ToRgba(borderColor) : borderColor,
          borderWidth: showBorder ? `${borderWidth}px` : '0px',
        };
      },

      getEventCardStyle() {
        const self = this;
        return function (event) {
          const config = self.calendarConfig;
          const bgColor =
            config.eventBackgroundColor || calendarDefaultOptions.eventBackgroundColor;

          return {
            backgroundColor: bgColor && isHex8(bgColor) ? hex8ToRgba(bgColor) : bgColor,
          };
        };
      },

      eventDateStyle() {
        const config = this.calendarConfig;
        const bgColor =
          config.eventDateBackgroundColor || calendarDefaultOptions.eventDateBackgroundColor;

        return {
          backgroundColor: bgColor && isHex8(bgColor) ? hex8ToRgba(bgColor) : bgColor,
        };
      },

      eventTitleStyle() {
        const config = this.calendarConfig;
        const fontColor = config.eventTitleColor || calendarDefaultOptions.eventTitleColor;

        return {
          fontFamily: config.eventTitleFont || calendarDefaultOptions.eventTitleFont,
          fontSize: `${config.eventTitleSize || calendarDefaultOptions.eventTitleSize}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
        };
      },

      eventTimeStyle() {
        const config = this.calendarConfig;
        const fontColor = config.eventTimeColor || calendarDefaultOptions.eventTimeColor;

        return {
          fontFamily: config.eventTimeFont || calendarDefaultOptions.eventTimeFont,
          fontSize: `${config.eventTimeSize || calendarDefaultOptions.eventTimeSize}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
        };
      },

      eventDescriptionStyle() {
        const config = this.calendarConfig;
        const fontColor =
          config.eventDescriptionColor || calendarDefaultOptions.eventDescriptionColor;

        return {
          fontFamily: config.eventDescriptionFont || calendarDefaultOptions.eventDescriptionFont,
          fontSize: `${
            config.eventDescriptionSize || calendarDefaultOptions.eventDescriptionSize
          }px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
        };
      },

      weekdayHeaderStyle() {
        const config = this.calendarConfig;
        const fontColor = config.weekdayHeaderColor || calendarDefaultOptions.weekdayHeaderColor;

        return {
          fontFamily: config.weekdayHeaderFont || calendarDefaultOptions.weekdayHeaderFont,
          fontSize: `${config.weekdayHeaderSize || calendarDefaultOptions.weekdayHeaderSize}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
        };
      },

      dateHeaderStyle() {
        const config = this.calendarConfig;
        const fontColor = config.dateHeaderColor || calendarDefaultOptions.dateHeaderColor;

        return {
          fontFamily: config.dateHeaderFont || calendarDefaultOptions.dateHeaderFont,
          fontSize: `${config.dateHeaderSize || calendarDefaultOptions.dateHeaderSize}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
        };
      },

      listDateDayStyle() {
        const config = this.calendarConfig;
        const fontColor = config.listDateDayColor || calendarDefaultOptions.listDateDayColor;

        return {
          fontFamily: config.listDateDayFont || calendarDefaultOptions.listDateDayFont,
          fontSize: `${config.listDateDaySize || calendarDefaultOptions.listDateDaySize}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
        };
      },

      listDateStyle() {
        const config = this.calendarConfig;
        const fontColor = config.listDateColor || calendarDefaultOptions.listDateColor;

        return {
          fontFamily: config.listDateFont || calendarDefaultOptions.listDateFont,
          fontSize: `${config.listDateSize || calendarDefaultOptions.listDateSize}px`,
          color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
        };
      },

      calendarFontHighlightColor() {
        const config = this.calendarConfig;
        return (
          config.calendarFontHighlightColor || calendarDefaultOptions.calendarFontHighlightColor
        );
      },

      calendarHighlightBackgroundColor() {
        const config = this.calendarConfig;
        return (
          config.calendarHighlightBackgroundColor ||
          calendarDefaultOptions.calendarHighlightBackgroundColor
        );
      },

      listHighlightDateBackgroundColor() {
        const config = this.calendarConfig;
        return (
          config.listHighlightDateBackgroundColor ||
          calendarDefaultOptions.listHighlightDateBackgroundColor
        );
      },

      listHighlightDateNumberColor() {
        const config = this.calendarConfig;
        return (
          config.listHighlightDateNumberColor || calendarDefaultOptions.listHighlightDateNumberColor
        );
      },

      getEventDateStyle() {
        const self = this;
        return function (event) {
          const config = self.calendarConfig;
          const isCurrentDay = self.isToday(event.startDate);
          const showBorder = config.eventBorderDisplay !== false;
          const borderWidth =
            config.eventBorderWidth !== undefined
              ? config.eventBorderWidth
              : calendarDefaultOptions.eventBorderWidth;
          const borderColor = config.eventBorderColor || calendarDefaultOptions.eventBorderColor;
          const bgColor =
            config.eventDateBackgroundColor || calendarDefaultOptions.eventDateBackgroundColor;
          let backgroundColor = bgColor && isHex8(bgColor) ? hex8ToRgba(bgColor) : bgColor;

          const baseStyle = {
            backgroundColor: backgroundColor,
          };

          if (isCurrentDay) {
            const highlightBg = self.listHighlightDateBackgroundColor;
            backgroundColor =
              highlightBg && isHex8(highlightBg) ? hex8ToRgba(highlightBg) : highlightBg;
            baseStyle.backgroundColor = backgroundColor;
            baseStyle.borderTopLeftRadius = '8px';
            baseStyle.borderBottomLeftRadius = '8px';
            baseStyle.border = 'none';
          } else if (showBorder) {
            const borderColorProcessed =
              borderColor && isHex8(borderColor) ? hex8ToRgba(borderColor) : borderColor;
            baseStyle.borderTop = `${borderWidth}px solid ${borderColorProcessed}`;
            baseStyle.borderBottom = `${borderWidth}px solid ${borderColorProcessed}`;
            baseStyle.borderLeft = `${borderWidth}px solid ${borderColorProcessed}`;
          }

          return baseStyle;
        };
      },

      eventContentStyle() {
        const config = this.calendarConfig;
        const showBorder = config.eventBorderDisplay !== false;
        const borderWidth =
          config.eventBorderWidth !== undefined
            ? config.eventBorderWidth
            : calendarDefaultOptions.eventBorderWidth;
        const borderColor = config.eventBorderColor || calendarDefaultOptions.eventBorderColor;

        if (!showBorder) {
          return {};
        }

        const borderColorProcessed =
          borderColor && isHex8(borderColor) ? hex8ToRgba(borderColor) : borderColor;

        return {
          borderRadius: '0 8px 8px 0',
          borderTop: `${borderWidth}px solid ${borderColorProcessed}`,
          borderBottom: `${borderWidth}px solid ${borderColorProcessed}`,
          borderRight: `${borderWidth}px solid ${borderColorProcessed}`,
        };
      },

      getListDateDayStyle() {
        const self = this;
        return function (event) {
          const config = self.calendarConfig;
          const fontColor = config.listDateDayColor || calendarDefaultOptions.listDateDayColor;
          let color = fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor;

          if (self.isToday(event.startDate)) {
            const highlightColor = self.listHighlightDateNumberColor;
            color =
              highlightColor && isHex8(highlightColor)
                ? hex8ToRgba(highlightColor)
                : highlightColor;
          }

          return {
            fontFamily: config.listDateDayFont || calendarDefaultOptions.listDateDayFont,
            fontSize: `${config.listDateDaySize || calendarDefaultOptions.listDateDaySize}px`,
            color: color,
          };
        };
      },

      getWeekdayHeaderStyle() {
        const self = this;
        return function (day) {
          const config = self.calendarConfig;
          const fontColor = config.weekdayHeaderColor || calendarDefaultOptions.weekdayHeaderColor;
          let color = fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor;

          const dayIndex = self.calendarDays.indexOf(day);
          const dayDate = moment(self.now).startOf('day').add(dayIndex, 'days');
          if (self.isToday(dayDate)) {
            const highlightColor = self.calendarFontHighlightColor;
            color =
              highlightColor && isHex8(highlightColor)
                ? hex8ToRgba(highlightColor)
                : highlightColor;
          }

          return {
            fontFamily: config.weekdayHeaderFont || calendarDefaultOptions.weekdayHeaderFont,
            fontSize: `${config.weekdayHeaderSize || calendarDefaultOptions.weekdayHeaderSize}px`,
            color: color,
            backgroundColor: 'transparent',
          };
        };
      },

      getDateHeaderStyle() {
        const self = this;
        return function (day) {
          const config = self.calendarConfig;
          const fontColor = config.dateHeaderColor || calendarDefaultOptions.dateHeaderColor;
          let color = fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor;

          const dayIndex = self.calendarDays.indexOf(day);
          const dayDate = moment(self.now).startOf('day').add(dayIndex, 'days');
          if (self.isToday(dayDate)) {
            const highlightColor = self.calendarFontHighlightColor;
            color =
              highlightColor && isHex8(highlightColor)
                ? hex8ToRgba(highlightColor)
                : highlightColor;
          }

          return {
            fontFamily: config.dateHeaderFont || calendarDefaultOptions.dateHeaderFont,
            fontSize: `${config.dateHeaderSize || calendarDefaultOptions.dateHeaderSize}px`,
            color: color,
            backgroundColor: 'transparent',
          };
        };
      },

      getDayColumnStyle() {
        const self = this;
        return function (day) {
          const dayIndex = self.calendarDays.indexOf(day);
          const dayDate = moment(self.now).startOf('day').add(dayIndex, 'days');

          if (self.isToday(dayDate)) {
            const bgColor = self.calendarHighlightBackgroundColor;
            return {
              backgroundColor: bgColor && isHex8(bgColor) ? hex8ToRgba(bgColor) : bgColor,
            };
          }

          return {};
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .calendar-widget {
    box-sizing: border-box;
    padding: 4px 4px 3px;
  }

  .calendar-events {
    display: flex;
    flex-direction: column;
  }

  .calendar-event-card {
    display: flex;
    flex-direction: row;
    overflow: hidden;
    padding: 0;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .calendar-event-date {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 16px 12px;
    background-color: #f0f0f0;
    width: 120px;
    flex-shrink: 0;
  }

  .date-month {
    font-size: 12px;
    font-weight: 600;
    color: #666666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .date-day {
    font-size: 32px;
    font-weight: 700;
    color: #333333;
    line-height: 1;
    margin-bottom: 4px;
  }

  .date-weekday {
    font-size: 11px;
    font-weight: 500;
    color: #666666;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .calendar-event-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 16px 20px;
    flex: 1;
  }

  .event-title {
    font-weight: 700;
    font-size: 24px;
    color: #1a1a1a;
    line-height: 1.2;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .event-time {
    font-size: 14px;
    font-weight: 500;
    color: #4a5568;
    margin-bottom: 4px;
  }

  .event-description {
    font-size: 14px;
    font-weight: 400;
    color: #6b7280;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .calendar-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    justify-content: center;
    column-gap: 12px;
    row-gap: 12px;
    padding: 40px 16px;
    color: #666666;

    i {
      font-size: 48px;
    }

    .loading-message {
      font-size: 14px;
      font-weight: 500;
    }
  }

  .calendar-no-events {
    display: flex;
    height: 100%;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    column-gap: 12px;
    row-gap: 12px;
    padding: 40px 16px;
    color: #999999;

    i {
      font-size: 48px;
    }

    .no-events-message {
      font-size: 14px;
      font-weight: 500;
    }
  }

  .calendar-grid {
    display: flex;
    flex-direction: row;
    height: 100%;
    padding: 8px;

    &[data-days='1'] .calendar-day-column {
      flex: 1;
    }

    &[data-days='2'] .calendar-day-column {
      flex: 1;
      max-width: 50%;
    }

    &[data-days='3'] .calendar-day-column {
      flex: 1;
      max-width: 33.333%;
    }

    &[data-days='4'] .calendar-day-column {
      flex: 1;
      max-width: 25%;
    }

    &[data-days='5'] .calendar-day-column {
      flex: 1;
      max-width: 20%;
    }
  }

  .calendar-day-column {
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    overflow: hidden;
  }

  .day-header {
    background-color: #ffffff;
    padding: 16px;
    text-align: center;
  }

  .day-weekday {
    font-size: 14px;
    font-weight: 500;
    color: #6c757d;
    margin-bottom: 4px;
  }

  .day-date {
    font-size: 18px;
    font-weight: 600;
    color: #2c3e50;
  }

  .day-events {
    flex: 1;
    padding: 8px;
    overflow-y: hidden;
  }

  .calendar-grid-event {
    background-color: #ffffff;
    border-radius: 8px;
    padding: 12px;
    margin-bottom: 8px;
    border: 2px solid #f0f0f0;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .grid-event-title {
    font-size: 16px;
    font-weight: 600;
    color: #2c3e50;
    margin-bottom: 6px;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .grid-event-time {
    font-size: 13px;
    font-weight: 500;
    color: #e3003a;
    margin-bottom: 4px;
  }

  .grid-event-description {
    font-size: 12px;
    color: #6c757d;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .no-events-day {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 24px 12px;
    column-gap: 4px;
    color: #adb5bd;
    font-size: 14px;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .rotating {
    animation: rotate 1s linear infinite;
  }
</style>
